import { useEffect, useRef, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { getDeviceId } from '../../utils/access';
import { adoptAccount, type AccountPayload } from '../../utils/account';
import { drawQR } from '../../utils/qr';
import { isCarBrowser } from '../../utils/platform';
import { useT, getLang } from '../../i18n';

/**
 * AccountModal — sign in = sign up: email → 6-digit code, no password.
 * On a big screen (the Tesla) it also shows a QR: scan it, finish on the phone,
 * and this screen signs in by itself (server hands the account over once).
 */
export function AccountModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [step, setStep] = useState<'email' | 'code' | 'done'>('email');
  // ONE flow (email → code) — sign in and sign up are the same thing here, and pretending otherwise with
  // tabs was theatre (Marc called it). The SERVER tells us afterwards whether this verify CREATED the
  // account, and the done screen greets new vs returning with that truth.
  const [wasCreated, setWasCreated] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState(false);   // the news box: never ticked for them
  const [error, setError] = useState('');
  const [doneEmail, setDoneEmail] = useState('');
  // QR flow: phones don't need it; the car (or any wide screen) gets it
  const wantQr = isCarBrowser() || (typeof window !== 'undefined' && window.innerWidth >= 700);
  // 'By continuing you accept the {terms} and the {privacy}.' → the three pieces of plain text around the links
  const termsLine = t('termsLine', { terms: '\u0000', privacy: '\u0001' }).split(/[\u0000\u0001]/);
  const [linkUrl, setLinkUrl] = useState('');
  const qrRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!wantQr) return;
    let stop = false;
    let poll: ReturnType<typeof setInterval> | null = null;
    const arm = async () => {
      try {
        const r = await fetch('/api/auth/link/new', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ deviceId: getDeviceId() }) });
        if (!r.ok || stop) return;
        const j = (await r.json()) as { code: string; url: string };
        setLinkUrl(j.url);
        if (poll) clearInterval(poll);
        poll = setInterval(async () => {
          try {
            const s = await fetch(`/api/auth/link/status?code=${encodeURIComponent(j.code)}`);
            if (s.status === 404) { if (poll) clearInterval(poll); setLinkUrl(''); void arm(); return; } // expired → fresh QR
            const d = (await s.json()) as { status: string; account?: AccountPayload };
            if (d.status === 'done' && d.account) {
              if (poll) clearInterval(poll);
              await adoptAccount(d.account);
              setWasCreated(!!d.account.created);
              setDoneEmail(d.account.email);
              setStep('done');
              setTimeout(onClose, 1500);
            }
          } catch { /* keep polling */ }
        }, 2000);
      } catch { /* no QR, email still works */ }
    };
    void arm();
    return () => { stop = true; if (poll) clearInterval(poll); };
  }, [wantQr]);
  useEffect(() => {
    if (linkUrl && qrRef.current) drawQR(qrRef.current, linkUrl, 180);
  }, [linkUrl]);

  const sendOtp = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await fetch('/api/auth/otp', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? 'error');
      setStep('code');
    } catch {
      setError(t('errSendCode'));
    }
    setBusy(false);
  };

  // THE SIXTH DIGIT IS THE BUTTON. Typing a code in a car is awkward enough, and the phone offers the
  // code from the e-mail itself: the moment six digits are there, they go. Each value is tried once, so
  // a wrong code does not loop, and the button stays for anyone who prefers it.
  const tried = useRef('');
  useEffect(() => {
    if (step !== 'code' || busy || code.length !== 6 || tried.current === code) return;
    tried.current = code;
    void verify();
  }, [code, step, busy]);

  const verify = async (e?: Event) => {
    e?.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await fetch('/api/auth/verify', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, code, deviceId: getDeviceId() }),
      });
      if (!r.ok) throw new Error('bad code');
      const j = (await r.json()) as AccountPayload;
      await adoptAccount({ ...j, email });
      // consent belongs to the account, and so does the moment the terms were accepted
      void fetch('/api/consent', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ deviceId: getDeviceId(), notify, lang: getLang() }) }).catch(() => undefined);
      setWasCreated(!!j.created);
      setDoneEmail(email);
      setStep('done');
      setTimeout(onClose, 1200);
    } catch {
      setError(t('errBadCode'));
    }
    setBusy(false);
  };

  // portaled to <body>: inside #app an ancestor's stacking context let the fixed menu burger paint on top
  return createPortal(
    <div class="acct" onClick={(e) => e.stopPropagation()}>
      <div class={`acct__card ${wantQr && step !== 'done' ? 'acct__card--wide' : ''}`}>
        <div class="acct__head">
          <span>{t('signInSignUp')}</span>
          <button onClick={onClose}>✕</button>
        </div>
        {step === 'done' ? (
          <p class="acct__ok">✓ {wasCreated ? t('accountCreated') : t('welcomeBack')}: {doneEmail}<br /><small>{wasCreated ? 'your cars & settings will follow this email everywhere' : 'cars & settings restored'}</small></p>
        ) : (
          <div class={wantQr ? 'acct__cols' : ''}>
            {wantQr && (
              <div class="acct__qrcol">
                <div class="acct__qrwrap">{linkUrl ? <canvas ref={qrRef} width={180} height={180} /> : <div class="acct__qrwait">…</div>}</div>
                <p class="acct__hint"><b>{t('scanPhone')}</b> {t('scanPhoneBody')}</p>
              </div>
            )}
            <div class="acct__formcol">
              {wantQr && <div class="acct__or">{t('orTypeHere')}</div>}
              {step === 'email' && (
                <form class="acct__form" onSubmit={sendOtp}>
                  <p class="acct__hint">{t('emailIsAccount', { is: t('is') })}</p>
                  <input class="acct__input" type="email" inputMode="email" autocomplete="email" placeholder="you@email.com" value={email} onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)} autoFocus={!wantQr} />
                  {/* UNTICKED, and it stays unticked unless the driver ticks it: a new engine is marketing,
                      and marketing to someone who has not bought needs their word for it. */}
                  <label class="acct__check"><input type="checkbox" checked={notify} onChange={(e) => setNotify((e.currentTarget as HTMLInputElement).checked)} /><span>{t('notifyMe')}</span></label>
                  <button class="acct__btn" disabled={busy || !email}>{busy ? t('sending') : t('continueBtn')}</button>
                  <p class="acct__legal">{termsLine[0]}<a href="/legal/terms" target="_blank" rel="noreferrer">{t('termsWord')}</a>{termsLine[1]}<a href="/legal/privacy" target="_blank" rel="noreferrer">{t('privacyWord')}</a>{termsLine[2]}</p>
                </form>
              )}
              {step === 'code' && (
                <form class="acct__form" onSubmit={verify}>
                  <p class="acct__hint">{t('weEmailed', { email })}</p>
                  <input class="acct__input acct__input--code" inputMode="numeric" pattern="[0-9]*" autocomplete="one-time-code" placeholder="123456" maxLength={6} value={code}
                    onInput={(e) => setCode((e.currentTarget as HTMLInputElement).value.replace(/\D/g, '').slice(0, 6))} autoFocus />
                  <button class="acct__btn" disabled={busy || code.length < 4}>{busy ? t('checking') : t('verify')}</button>
                  <button type="button" class="acct__link" onClick={() => { setStep('email'); setCode(''); }}>{t('changeEmail')}</button>
                </form>
              )}
              {error && <p class="acct__err">{error}</p>}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
